/**
 * Tenant Admin → Catalog page (Spec 004 Story 1 + Spec 016).
 *
 * Three-tab list view: Materials / Labor / ZIP factors. Each tab has
 * a CSV importer + a list table. Spec 016 adds single-row editing:
 * a MaterialEditor modal (create + edit), and inline expand-row
 * editors for labor rates and ZIP factors.
 */

import { Fragment, useCallback, useEffect, useState } from "react";

import { CsvImporter } from "@/components/catalog/CsvImporter";
import { IntentEditor } from "@/components/catalog/IntentEditor";
import { LaborRowEditor } from "@/components/catalog/LaborRowEditor";
import { MaterialEditor } from "@/components/catalog/MaterialEditor";
import { ZipRowEditor } from "@/components/catalog/ZipRowEditor";
import {
  listLabor,
  listMaterials,
  listRemodelIntents,
  listZipFactors,
  setMaterialActive,
} from "@starz/shared/api/sdk.gen";
import type {
  LaborRate,
  Material,
  RemodelIntent,
  ZipFactor,
} from "@starz/shared/api/types.gen";

type Tab = "materials" | "labor" | "zip_factors" | "intents";

const TAB_LABEL: Record<Tab, string> = {
  materials: "Materials",
  labor: "Labor rates",
  zip_factors: "ZIP factors",
  intents: "Intents",
};

const KIND_BY_TAB: Record<Tab, "material" | "labor" | "zip_factor"> = {
  materials: "material",
  labor: "labor",
  zip_factors: "zip_factor",
  // Intents have no CSV importer; we still need a key for the existing
  // KIND_BY_TAB lookup. Map to "material" as a harmless default; the
  // tab body short-circuits to skip the importer.
  intents: "material",
};

export function AdminCatalog() {
  const [tab, setTab] = useState<Tab>("materials");
  const [materials, setMaterials] = useState<Material[]>([]);
  const [labor, setLabor] = useState<LaborRate[]>([]);
  const [zip, setZip] = useState<ZipFactor[]>([]);
  const [intents, setIntents] = useState<RemodelIntent[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [m, l, z, i] = await Promise.all([
      listMaterials(),
      listLabor(),
      listZipFactors(),
      listRemodelIntents(),
    ]);
    if (!("error" in m)) setMaterials(m.data);
    if (!("error" in l)) setLabor(l.data);
    if (!("error" in z)) setZip(z.data);
    if (!("error" in i)) setIntents(i.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="mx-auto max-w-content px-6 py-12 md:px-12 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Catalog
      </p>
      <h1 className="mb-3 font-serif text-4xl text-starz-navy">
        Your <em className="text-starz-gold">price book</em>.
      </h1>
      <div className="mb-6 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      <div className="mb-6 flex gap-2 border-b border-starz-gold/15">
        {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={
              tab === t
                ? "border-b-2 border-starz-gold px-3 py-2 text-sm font-semibold text-starz-navy"
                : "px-3 py-2 text-sm text-warm-gray hover:text-starz-charcoal"
            }
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {tab !== "intents" ? (
        <div className="mb-6">
          <CsvImporter kind={KIND_BY_TAB[tab]} onCommitted={() => void refresh()} />
        </div>
      ) : null}

      {loading ? (
        <p className="text-warm-gray">Loading…</p>
      ) : tab === "materials" ? (
        <MaterialsTable
          rows={materials}
          laborTaskIds={labor.map((l) => l.task_id)}
          onChanged={() => void refresh()}
        />
      ) : tab === "labor" ? (
        <LaborTable rows={labor} onChanged={() => void refresh()} />
      ) : tab === "zip_factors" ? (
        <ZipTable rows={zip} onChanged={() => void refresh()} />
      ) : (
        <IntentsTable rows={intents} onChanged={() => void refresh()} />
      )}
    </div>
  );
}

function IntentsTable({
  rows,
  onChanged,
}: {
  rows: RemodelIntent[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<RemodelIntent | "new" | null>(null);
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="rounded-md bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white hover:opacity-90"
        >
          + Add intent
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/40 p-8 text-center text-sm text-warm-gray">
          No intents yet. Platform defaults will be used until you add or override one.
        </div>
      ) : (
        <table className="min-w-full divide-y divide-starz-gold/15 rounded-xl border border-starz-gold/15 bg-white text-sm">
          <thead className="bg-starz-cream/60 text-left text-xs uppercase tracking-widest text-starz-charcoal">
            <tr>
              <th className="px-5 py-3">Photo</th>
              <th className="px-5 py-3">ID / display name</th>
              <th className="px-5 py-3">Room type</th>
              <th className="px-5 py-3">Sort</th>
              <th className="px-5 py-3">Active</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-starz-gold/10">
            {rows.map((r) => (
              <tr key={r.intent_id}>
                <td className="px-5 py-2">
                  {r.photo_url ? (
                    <img
                      src={r.photo_url}
                      alt={r.display_name}
                      className="h-12 w-12 rounded object-cover"
                    />
                  ) : (
                    <div className="grid h-12 w-12 place-items-center rounded bg-starz-cream text-xs text-warm-gray">
                      —
                    </div>
                  )}
                </td>
                <td className="px-5 py-2">
                  <div className="font-medium text-starz-navy">{r.display_name}</div>
                  <div className="text-xs text-warm-gray">{r.intent_id}</div>
                </td>
                <td className="px-5 py-2 text-warm-gray">{r.default_room_type ?? "—"}</td>
                <td className="px-5 py-2 font-mono text-warm-gray">{r.sort_order}</td>
                <td className="px-5 py-2 text-xs">
                  {r.active ? (
                    <span className="rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-900">
                      Active
                    </span>
                  ) : (
                    <span className="rounded-full bg-starz-cream px-3 py-1 font-medium text-warm-gray">
                      Hidden
                    </span>
                  )}
                </td>
                <td className="px-5 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className="rounded-md border border-starz-gold/30 px-3 py-1 text-xs font-medium text-starz-navy hover:bg-starz-cream"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing !== null ? (
        <IntentEditor
          intent={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function _formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function MaterialsTable({
  rows,
  laborTaskIds,
  onChanged,
}: {
  rows: Material[];
  laborTaskIds: string[];
  onChanged: () => void;
}) {
  // `null` = closed; `"new"` = create mode; a Material = edit mode.
  const [editing, setEditing] = useState<Material | "new" | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="rounded-md bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white hover:opacity-90"
        >
          + Add material
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/40 p-8 text-center text-sm text-warm-gray">
          No materials yet. Add one above or import a CSV.
        </div>
      ) : (
        <table className="min-w-full divide-y divide-starz-gold/15 rounded-xl border border-starz-gold/15 bg-white text-sm">
          <thead className="bg-starz-cream/60 text-left text-xs uppercase tracking-widest text-starz-charcoal">
            <tr>
              <th className="px-5 py-3">Photo</th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Group</th>
              <th className="px-5 py-3">Mode</th>
              <th className="px-5 py-3">Unit</th>
              <th className="px-5 py-3">Price</th>
              <th className="px-5 py-3">Active</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-starz-gold/10">
            {rows.map((r) => (
              <tr key={r.material_id}>
                <td className="px-5 py-2">
                  {r.photo_url ? (
                    <img
                      src={r.photo_url}
                      alt={r.display_name}
                      className="h-12 w-12 rounded object-cover"
                    />
                  ) : (
                    <div className="grid h-12 w-12 place-items-center rounded bg-starz-cream text-xs text-warm-gray">
                      —
                    </div>
                  )}
                </td>
                <td className="px-5 py-2">
                  <div className="font-medium text-starz-navy">{r.display_name}</div>
                  <div className="text-xs text-warm-gray">
                    {r.material_id} · {r.category}
                    {r.subcategory ? ` / ${r.subcategory}` : ""}
                  </div>
                </td>
                <td className="px-5 py-2 text-warm-gray">{r.option_group_name ?? "—"}</td>
                <td className="px-5 py-2 text-warm-gray">{r.pricing_mode}</td>
                <td className="px-5 py-2 text-warm-gray">{r.unit}</td>
                <td className="px-5 py-2 font-mono text-starz-charcoal">
                  {_formatPrice(r.unit_price_cents)}
                </td>
                <td className="px-5 py-2">
                  <button
                    type="button"
                    onClick={async () => {
                      await setMaterialActive({
                        materialId: r.material_id,
                        active: !r.active,
                      });
                      onChanged();
                    }}
                    className={
                      r.active
                        ? "rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-900"
                        : "rounded-full bg-starz-cream px-3 py-1 text-xs font-medium text-warm-gray"
                    }
                  >
                    {r.active ? "Active" : "Hidden"}
                  </button>
                </td>
                <td className="px-5 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className="rounded-md border border-starz-gold/30 px-3 py-1 text-xs font-medium text-starz-navy hover:bg-starz-cream"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing !== null ? (
        <MaterialEditor
          material={editing === "new" ? null : editing}
          materialIds={rows.map((r) => r.material_id)}
          laborTaskIds={laborTaskIds}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function LaborTable({ rows, onChanged }: { rows: LaborRate[]; onChanged: () => void }) {
  const [editingId, setEditingId] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/40 p-8 text-center text-sm text-warm-gray">
        No labor rates yet. Import a CSV to get started.
      </div>
    );
  }
  return (
    <table className="min-w-full divide-y divide-starz-gold/15 rounded-xl border border-starz-gold/15 bg-white text-sm">
      <thead className="bg-starz-cream/60 text-left text-xs uppercase tracking-widest text-starz-charcoal">
        <tr>
          <th className="px-5 py-3">Task</th>
          <th className="px-5 py-3">Unit</th>
          <th className="px-5 py-3">Price</th>
          <th className="px-5 py-3">Notes</th>
          <th className="px-5 py-3" />
        </tr>
      </thead>
      <tbody className="divide-y divide-starz-gold/10">
        {rows.map((r) => (
          <Fragment key={r.task_id}>
            <tr>
              <td className="px-5 py-2">
                <div className="font-medium text-starz-navy">{r.display_name}</div>
                <div className="text-xs text-warm-gray">{r.task_id}</div>
              </td>
              <td className="px-5 py-2 text-warm-gray">{r.unit}</td>
              <td className="px-5 py-2 font-mono text-starz-charcoal">
                {_formatPrice(r.unit_price_cents)}
              </td>
              <td className="px-5 py-2 text-xs text-warm-gray">{r.notes ?? "—"}</td>
              <td className="px-5 py-2 text-right">
                <button
                  type="button"
                  onClick={() =>
                    setEditingId(editingId === r.task_id ? null : r.task_id)
                  }
                  className="rounded-md border border-starz-gold/30 px-3 py-1 text-xs font-medium text-starz-navy hover:bg-starz-cream"
                >
                  {editingId === r.task_id ? "Close" : "Edit"}
                </button>
              </td>
            </tr>
            {editingId === r.task_id ? (
              <tr>
                <td colSpan={5} className="px-5 py-3">
                  <LaborRowEditor
                    rate={r}
                    onCancel={() => setEditingId(null)}
                    onSaved={() => {
                      setEditingId(null);
                      onChanged();
                    }}
                  />
                </td>
              </tr>
            ) : null}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function ZipTable({ rows, onChanged }: { rows: ZipFactor[]; onChanged: () => void }) {
  const [editingZip, setEditingZip] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-starz-gold/15 bg-starz-cream/40 p-8 text-center text-sm text-warm-gray">
        No ZIP factors configured. Default factor 1.0 will be used for any ZIP.
      </div>
    );
  }
  return (
    <table className="min-w-full divide-y divide-starz-gold/15 rounded-xl border border-starz-gold/15 bg-white text-sm">
      <thead className="bg-starz-cream/60 text-left text-xs uppercase tracking-widest text-starz-charcoal">
        <tr>
          <th className="px-5 py-3">ZIP</th>
          <th className="px-5 py-3">Factor</th>
          <th className="px-5 py-3">Notes</th>
          <th className="px-5 py-3">Active</th>
          <th className="px-5 py-3" />
        </tr>
      </thead>
      <tbody className="divide-y divide-starz-gold/10">
        {rows.map((r) => (
          <Fragment key={r.zip}>
            <tr>
              <td className="px-5 py-2 font-mono text-starz-navy">{r.zip}</td>
              <td className="px-5 py-2 font-mono text-starz-charcoal">
                {r.factor.toFixed(3)}
              </td>
              <td className="px-5 py-2 text-xs text-warm-gray">{r.notes ?? "—"}</td>
              <td className="px-5 py-2 text-xs">
                {r.active ? (
                  <span className="rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-900">
                    Active
                  </span>
                ) : (
                  <span className="rounded-full bg-starz-cream px-3 py-1 font-medium text-warm-gray">
                    Hidden
                  </span>
                )}
              </td>
              <td className="px-5 py-2 text-right">
                <button
                  type="button"
                  onClick={() => setEditingZip(editingZip === r.zip ? null : r.zip)}
                  className="rounded-md border border-starz-gold/30 px-3 py-1 text-xs font-medium text-starz-navy hover:bg-starz-cream"
                >
                  {editingZip === r.zip ? "Close" : "Edit"}
                </button>
              </td>
            </tr>
            {editingZip === r.zip ? (
              <tr>
                <td colSpan={5} className="px-5 py-3">
                  <ZipRowEditor
                    factor={r}
                    onCancel={() => setEditingZip(null)}
                    onSaved={() => {
                      setEditingZip(null);
                      onChanged();
                    }}
                  />
                </td>
              </tr>
            ) : null}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
